/**
 * 業者アプリ データアクセス — Supabase 直叩き（RLS適用）。Hono API非依存。
 * open案件の閲覧は RLS(is_vendor) によりロール vendor が必要。
 */
import type { Case, Bid, CreateBidRequest, CustomerSpecs } from "@shitamy/types";
import { supabase } from "./supabase";

/** 直チャットのメッセージ（sql/12 messages テーブル）。落札案件の当事者のみ閲覧/投稿可（RLS）。 */
export interface ChatMessage {
  id: string;
  case_id: string;
  sender_id: string;
  sender_role: "customer" | "vendor" | "admin";
  body: string | null;
  image_url: string | null;
  created_at: string;
}

/** 1案件あたりの応札枠（社長方針「早い者勝ち・3社まで」）。sql/10 のトリガと一致させること。 */
export const BID_CAP = 3;

async function currentUserId(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const uid = data.session?.user.id;
  if (!uid) throw new Error("ログインが必要です");
  return uid;
}

export const api = {
  async listOpenCases(
    opts: { ac_type?: string; pref?: string; order?: "new" | "closing" } = {},
  ): Promise<Case[]> {
    let q = supabase
      .from("cases")
      .select("*")
      .eq("status", "open")
      // closing=締切が近い順（入札ウィンドウ72hのため作成が古い順）、new=新着順
      .order("created_at", { ascending: opts.order === "closing" })
      .limit(100);
    if (opts.pref) q = q.eq("prefecture", opts.pref);
    if (opts.ac_type) q = q.eq("ac_type", opts.ac_type);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as Case[];
  },

  async getCase(id: string): Promise<Case> {
    const { data, error } = await supabase.from("cases").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as Case;
  },

  async listMyBids(): Promise<Bid[]> {
    const uid = await currentUserId();
    const { data, error } = await supabase
      .from("bids")
      .select("*")
      .eq("vendor_id", uid)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Bid[];
  },

  /**
   * 「要対応の受注」件数（下部ナビのバッジ用）。
   * RLS(cases_vendor_awarded)により自分が落札した案件のみ見える前提で、
   * まだ工事日確定していない(awarded)／完了報告していない(scheduled)案件を数える。
   * 濱田さんが「次にどこを押すか」で迷った問題への赤バッジ導線。
   */
  async countActionableOrders(): Promise<number> {
    const { count, error } = await supabase
      .from("cases")
      .select("id", { count: "exact", head: true })
      .in("status", ["awarded", "scheduled"]);
    if (error) return 0; // バッジは補助情報。失敗しても本体を止めない。
    return count ?? 0;
  },

  /**
   * 案件写真の閲覧用URL一覧（非訪問見積もりの主材料）。
   * case_photos は RLS で open案件 or 自分が落札した案件のみ参照可。
   * バケットは非公開のため60分の署名URLを発行する。
   */
  async listCasePhotoUrls(caseId: string): Promise<Array<{ path: string; url: string }>> {
    const { data, error } = await supabase
      .from("case_photos")
      .select("storage_path")
      .eq("case_id", caseId)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    const paths = (data ?? []).map((r) => r.storage_path as string);
    if (paths.length === 0) return [];
    const { data: signed, error: sErr } = await supabase.storage
      .from("case-photos")
      .createSignedUrls(paths, 3600);
    if (sErr) throw new Error(sErr.message);
    return (signed ?? [])
      .filter((s) => !!s.signedUrl)
      .map((s) => ({ path: s.path ?? "", url: s.signedUrl as string }));
  },

  /**
   * 複数案件の代表写真（1枚目）URL をまとめて取得（一覧カードのサムネイル用・N+1回避）。
   * case_id→署名URL のマップを返す。写真が無い案件はキー自体が無い。
   */
  async firstCasePhotoUrls(caseIds: string[]): Promise<Record<string, string>> {
    if (caseIds.length === 0) return {};
    const { data, error } = await supabase
      .from("case_photos")
      .select("case_id, storage_path, created_at")
      .in("case_id", caseIds)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    // 各案件で最初の1枚だけ採用（created_at 昇順の先頭）
    const firstPath: Record<string, string> = {};
    for (const r of (data ?? []) as Array<{ case_id: string; storage_path: string }>) {
      if (!firstPath[r.case_id]) firstPath[r.case_id] = r.storage_path;
    }
    const entries = Object.entries(firstPath);
    if (entries.length === 0) return {};
    const { data: signed, error: sErr } = await supabase.storage
      .from("case-photos")
      .createSignedUrls(entries.map(([, p]) => p), 3600);
    if (sErr) throw new Error(sErr.message);
    const map: Record<string, string> = {};
    entries.forEach(([caseId], i) => {
      const url = signed?.[i]?.signedUrl;
      if (url) map[caseId] = url;
    });
    return map;
  },

  /** 複数案件の表示用ブリーフ（受注管理：顧客名・管理番号・現場住所・種別）。case_id→情報 のマップ。 */
  async casesBrief(caseIds: string[]): Promise<Record<string, CaseBrief>> {
    if (caseIds.length === 0) return {};
    const { data, error } = await supabase
      .from("cases")
      .select("id, case_no, customer_name, ac_type, prefecture, city, address")
      .in("id", caseIds);
    if (error) throw new Error(error.message);
    const map: Record<string, CaseBrief> = {};
    for (const r of (data ?? []) as Array<CaseBrief & { id: string }>) {
      map[r.id] = { case_no: r.case_no, customer_name: r.customer_name, ac_type: r.ac_type, prefecture: r.prefecture, city: r.city, address: r.address };
    }
    return map;
  },

  /** 単一案件の pending 応札件数（残枠表示用・security definer RPC）。 */
  async casePendingCount(caseId: string): Promise<number> {
    const { data, error } = await supabase.rpc("case_pending_bid_count", { p_case_id: caseId });
    if (error) throw new Error(error.message);
    return (data as number) ?? 0;
  },

  /** 複数案件の pending 応札件数（一覧の残枠表示用・N+1回避）。case_id→件数 のマップを返す。 */
  async casePendingCounts(caseIds: string[]): Promise<Record<string, number>> {
    if (caseIds.length === 0) return {};
    const { data, error } = await supabase.rpc("case_pending_bid_counts", { p_case_ids: caseIds });
    if (error) throw new Error(error.message);
    const map: Record<string, number> = {};
    for (const r of (data ?? []) as Array<{ case_id: string; cnt: number }>) map[r.case_id] = r.cnt;
    return map;
  },

  /** 受注後：工事日を確定（awarded → scheduled）。RLS cases_vendor_awarded により自分の案件のみ。 */
  async confirmSchedule(caseId: string, dateISO: string): Promise<void> {
    const { error } = await supabase
      .from("cases")
      .update({ scheduled_date: dateISO, status: "scheduled" })
      .eq("id", caseId);
    if (error) throw new Error(error.message);
  },

  /**
   * 受注後：実機の最終確認値（品番/能力/冷媒）を登録（sql/21 SECURITY DEFINER RPC）。
   * cases.vendor_confirmed と training_samples.vendor_confirmed（AI精度評価の正解ラベル）に反映される。
   * 当該案件の落札業者本人のみ実行可。全項目空なら何もしない。
   */
  async reportVendorConfirmed(
    caseId: string,
    v: { model_number?: string | null; capacity?: string | null; refrigerant?: string | null; note?: string | null },
  ): Promise<void> {
    const { error } = await supabase.rpc("report_vendor_confirmed", {
      p_case_id: caseId,
      p_model_number: emptyToNull(v.model_number),
      p_capacity: emptyToNull(v.capacity),
      p_refrigerant: emptyToNull(v.refrigerant),
      p_note: emptyToNull(v.note),
    });
    if (error) throw new Error(error.message);
  },

  /**
   * 双方合意による取引削除（sql/33）。顧客・業者の両方が同意した時のみ削除。
   * returns: "requested"（相手の同意待ち）/ "deleted"（両者合意で削除実行）
   */
  async requestCaseDeletion(caseId: string): Promise<"requested" | "deleted"> {
    const { data, error } = await supabase.rpc("request_case_deletion", { p_case_id: caseId });
    if (error) throw new Error(error.message);
    return (data as "requested" | "deleted") ?? "requested";
  },
  /** 削除同意の取り下げ。 */
  async withdrawCaseDeletion(caseId: string): Promise<void> {
    const { error } = await supabase.rpc("withdraw_case_deletion", { p_case_id: caseId });
    if (error) throw new Error(error.message);
  },

  /** 受注後：工事完了を報告（scheduled → completed）。 */
  async reportCompleted(caseId: string): Promise<void> {
    const { error } = await supabase
      .from("cases")
      .update({ status: "completed" })
      .eq("id", caseId);
    if (error) throw new Error(error.message);
  },

  /**
   * 入札の作成。bids は (case_id, vendor_id) UNIQUE のため、
   * 同一案件への再入札は upsert で「既存入札の更新（金額・内訳・希望日の差し替え）」になる。
   * ※以前は insert のみで duplicate key エラー（23505）になっていた（2026-07-17 修正）。
   */
  async createBid(req: CreateBidRequest): Promise<Bid> {
    await currentUserId();
    // サーバ側で確実にupsert（既存があれば見積り出し直し=更新、新規はINSERTで先着枠判定）。
    // PostgRESTのupsert(onConflict)＋トリガの相性で出ていた 23505(重複キー) を根絶。
    const { data, error } = await supabase.rpc("place_bid", {
      p_case_id: req.case_id,
      p_amount: req.amount,
      p_comment: req.comment ?? null,
      p_proposed_date: req.proposed_date ?? null,
    });
    if (error) {
      if (error.code === "23505" || /duplicate key/i.test(error.message)) {
        throw new Error("入札の更新に失敗しました。ページを再読み込みして再度お試しください。");
      }
      throw new Error(error.message);
    }
    return data as Bid;
  },

  // === Chat（直チャット：業者⇔顧客） ===
  /** 案件の全メッセージを古い順で取得（RLSで当事者のみ）。 */
  async listMessages(caseId: string): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("case_id", caseId)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as ChatMessage[];
  },

  /** 業者としてメッセージ送信。sender_role='vendor'。 */
  async sendMessage(caseId: string, body: string): Promise<ChatMessage> {
    const uid = await currentUserId();
    const { data, error } = await supabase
      .from("messages")
      .insert({ case_id: caseId, sender_id: uid, sender_role: "vendor", body })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return data as ChatMessage;
  },

  /** 業者として画像メッセージを送信。case-photos に保存し長期署名URLを image_url に記録。 */
  async sendMessageImage(caseId: string, file: Blob): Promise<ChatMessage> {
    const uid = await currentUserId();
    const path = `${uid}/${Date.now()}_chat.jpg`;
    const up = await supabase.storage.from("case-photos").upload(path, file, { contentType: "image/jpeg", upsert: false });
    if (up.error) throw new Error(up.error.message);
    const { data: signed } = await supabase.storage.from("case-photos").createSignedUrl(path, 60 * 60 * 24 * 365);
    const url = signed?.signedUrl ?? path;
    const { data, error } = await supabase
      .from("messages")
      .insert({ case_id: caseId, sender_id: uid, sender_role: "vendor", body: null, image_url: url })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return data as ChatMessage;
  },

  // === AI判定の業者フィードバック ===
  /**
   * AI判定（品番/能力/冷媒）を業者が修正する。修正は「業者フィードバック」＝学習データの種。
   * - 品番→ai_model_guess、能力→ai_capacity（cases のフラット列）。
   * - 冷媒は cases にAI列が無いため customer_specs.refrigerant にマージして保持する。
   * RLS で弾かれる可能性はあるが、関数は用意し UI は動く形にする（エラーは呼び出し側で表示）。
   */
  async updateCaseAiFields(
    caseId: string,
    fields: { model?: string | null; capacity?: string | null; refrigerant?: string | null },
    prevSpecs?: CustomerSpecs | null,
  ): Promise<void> {
    const patch: Record<string, unknown> = {
      ai_model_guess: emptyToNull(fields.model),
      ai_capacity: emptyToNull(fields.capacity),
    };
    // 冷媒は customer_specs(jsonb) にマージ（既存値を壊さない）
    patch.customer_specs = {
      ...(prevSpecs ?? {}),
      refrigerant: emptyToNull(fields.refrigerant),
    } satisfies CustomerSpecs;

    const { error } = await supabase.from("cases").update(patch).eq("id", caseId);
    if (error) throw new Error(error.message);
  },

  /** 自分（業者）の振込先口座を取得（元請モデル：外注費の振込先）。 */
  async getMyBankAccount(): Promise<BankAccount | null> {
    const uid = await currentUserId();
    const { data, error } = await supabase
      .from("vendor_details")
      .select("bank, branch, account, account_holder, account_type")
      .eq("id", uid)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as BankAccount) ?? null;
  },

  /** 自分（業者）の振込先口座を登録・更新。実際の送金は支良洲工業の銀行振込（外注費）で行う。 */
  async saveMyBankAccount(v: BankAccount): Promise<void> {
    const uid = await currentUserId();
    const { error } = await supabase
      .from("vendor_details")
      .update({
        bank: emptyToNull(v.bank),
        branch: emptyToNull(v.branch),
        account: emptyToNull(v.account),
        account_holder: emptyToNull(v.account_holder),
        account_type: emptyToNull(v.account_type),
        updated_at: new Date().toISOString(),
      })
      .eq("id", uid);
    if (error) throw new Error(error.message);
  },

  // === 通知（notifications。RLSで本人分のみ） ===
  async listNotifications(): Promise<AppNotification[]> {
    const { data, error } = await supabase
      .from("notifications")
      .select("id, type, title, body, case_id, read, created_at")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);
    return (data ?? []) as AppNotification[];
  },
  async unreadNotificationCount(): Promise<number> {
    const { count, error } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("read", false);
    if (error) return 0;
    return count ?? 0;
  },
  async markAllNotificationsRead(): Promise<void> {
    const { error } = await supabase.from("notifications").update({ read: true }).eq("read", false);
    if (error) throw new Error(error.message);
  },
};

/** アプリ内通知。 */
export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  case_id: string | null;
  read: boolean;
  created_at: string;
}

/** 受注管理などの一覧表示用ブリーフ。 */
export interface CaseBrief {
  case_no: number | null;
  customer_name: string | null;
  ac_type: string | null;
  prefecture: string | null;
  city: string | null;
  address: string | null;
}

/** 振込先口座（外注費の受取口座）。 */
export interface BankAccount {
  bank: string | null;
  branch: string | null;
  account: string | null;
  account_holder: string | null;
  account_type: string | null;
}

function emptyToNull(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  return s === "" ? null : s;
}
